/**
 * Pictograms for wayfinding signs, all in the same 24x24 line style so they
 * sit together on a sign. Toilet figures, stairs, lift and first aid are drawn
 * here; the rest come from Lucide.
 */
import type { ComponentType, ReactElement, SVGProps } from 'react';
import {
  Baby,
  Banknote,
  Beer,
  Bike,
  ChefHat,
  Cigarette,
  CircleDot,
  ConciergeBell,
  Dices,
  Dog,
  DoorOpen,
  Flame,
  GlassWater,
  Info,
  LogIn,
  Martini,
  MicVocal,
  SquareParking,
  Target,
  ToyBrick,
  Trees,
  Tv,
  Utensils,
} from 'lucide-react';

type Props = SVGProps<SVGSVGElement> & { size?: number };

function make(paths: (props: { sw: number }) => ReactElement) {
  return function Pictogram({ size = 24, strokeWidth = 2, ...rest }: Props) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        {paths({ sw: Number(strokeWidth) })}
      </svg>
    );
  };
}

const ManFigure = ({ dx = 0, s = 1 }: { dx?: number; s?: number }) => (
  <g transform={`translate(${dx} 0) translate(12 12) scale(${s}) translate(-12 -12)`}>
    <circle cx="12" cy="3.6" r="1.9" fill="currentColor" stroke="none" />
    <path d="M9.6 21.2v-6.4M14.4 21.2v-6.4M8.4 14.2V9.6a2 2 0 0 1 2-2h3.2a2 2 0 0 1 2 2v4.6" />
  </g>
);

const WomanFigure = ({ dx = 0, s = 1 }: { dx?: number; s?: number }) => (
  <g transform={`translate(${dx} 0) translate(12 12) scale(${s}) translate(-12 -12)`}>
    <circle cx="12" cy="3.6" r="1.9" fill="currentColor" stroke="none" />
    <path d="M10.4 7.6h3.2l3 8.2H7.4z M10.6 15.8v5.4M13.4 15.8v5.4" />
  </g>
);

export const Man = make(() => <ManFigure />);
export const Woman = make(() => <WomanFigure />);
export const Toilets = make(() => (
  <>
    <ManFigure dx={-5.6} s={0.86} />
    <path d="M12 3.5v17" strokeOpacity={0.7} />
    <WomanFigure dx={5.6} s={0.86} />
  </>
));
export const Wheelchair = make(() => (
  <>
    <circle cx="10.2" cy="3.6" r="1.9" fill="currentColor" stroke="none" />
    <path d="M10.2 7.4v6.2h5.6l2.6 6" />
    <path d="M10.2 10.2h4.6" />
    <path d="M7.4 10.8a5.6 5.6 0 1 0 7.9 6.9" />
  </>
));
export const Stairs = make(() => <path d="M3 20.5h5v-5h4.5v-5H17V5.5h4" />);
export const Lift = make(() => (
  <>
    <rect x="5" y="2.5" width="14" height="19" rx="2" />
    <path d="m9 9.5 3-3 3 3M9 14.5l3 3 3-3" />
  </>
));
export const FirstAid = make(() => <path d="M12 5v14M5 12h14" strokeWidth={3.2} />);
export const Hanger = make(() => (
  <path d="M12 7.2a2.2 2.2 0 1 1 2.2-2.2c0 1.3-2.2 1.7-2.2 3.3v.7M3.5 18.5 12 12l8.5 6.5z" />
));
export const FireExit = make(() => (
  <>
    <path d="M13 3.5h6.5v17H13" />
    <circle cx="8.6" cy="4.6" r="1.8" fill="currentColor" stroke="none" />
    <path d="m4 12.6 2.6-3.4 3.6.6 2 3 2.3.5M8.4 15.2l1.8-5.4M8.4 15.2l-3 5.3M8.4 15.2l3.4 2.4v3" />
  </>
));

export const PICTOGRAMS: Record<string, ComponentType<Props>> = {
  wc: Toilets,
  woman: Woman,
  man: Man,
  wheelchair: Wheelchair,
  baby: Baby as ComponentType<Props>,
  beer: Beer as ComponentType<Props>,
  martini: Martini as ComponentType<Props>,
  utensils: Utensils as ComponentType<Props>,
  bell: ConciergeBell as ComponentType<Props>,
  chef: ChefHat as ComponentType<Props>,
  entrance: LogIn as ComponentType<Props>,
  exit: DoorOpen as ComponentType<Props>,
  'fire-exit': FireExit,
  'first-aid': FirstAid,
  stairs: Stairs,
  lift: Lift,
  garden: Trees as ComponentType<Props>,
  smoking: Cigarette as ComponentType<Props>,
  parking: SquareParking as ComponentType<Props>,
  bike: Bike as ComponentType<Props>,
  hanger: Hanger,
  cash: Banknote as ComponentType<Props>,
  water: GlassWater as ComponentType<Props>,
  music: MicVocal as ComponentType<Props>,
  tv: Tv as ComponentType<Props>,
  fire: Flame as ComponentType<Props>,
  pool: CircleDot as ComponentType<Props>,
  darts: Target as ComponentType<Props>,
  dice: Dices as ComponentType<Props>,
  play: ToyBrick as ComponentType<Props>,
  dog: Dog as ComponentType<Props>,
  info: Info as ComponentType<Props>,
};

export function Pictogram({ icon, ...props }: Props & { icon: string }) {
  const Component = PICTOGRAMS[icon] ?? (Info as ComponentType<Props>);
  return <Component {...props} />;
}
